import * as React from "react";
import { Gauge, Hash, Star, ThumbsDown, ThumbsUp } from "lucide-react";

import { cn } from "@/lib/utils";
import { Tooltip } from "@/components/ui";
import type { ReviewType } from "@/services/bedrock";

interface StarRatingProps extends React.ComponentProps<"div"> {
  rating: number;
  maxStars?: number;
  size?: "sm" | "default" | "lg";
}

type ReviewRatingSource = {
  starRating?: number | string | null;
  sourceSentiment?: string | null;
  tenPointRating?: number | null;
  npsScore?: number | null;
  npsCategory?: "promoter" | "passive" | "detractor" | string | null;
  /** Canonical rating type from the API; used when no filter-driven primaryRatingType is set. */
  ratingType?: ReviewType | string | null;
};

interface ReviewRatingProps extends React.ComponentProps<"div"> {
  review: ReviewRatingSource;
  size?: StarRatingProps["size"];
  fallback?: React.ReactNode;
  primaryRatingType?: ReviewType | null;
  secondaryRatingLayout?: "none" | "compact" | "stacked";
}

const starSizeClasses = {
  sm: "size-3.5",
  default: "size-5",
  lg: "size-6",
} as const;

const starGapClasses = {
  sm: "gap-0.5",
  default: "gap-0.5",
  lg: "gap-1",
} as const;

const sentimentIconSizeClasses = {
  sm: "size-5",
  default: "size-5",
  lg: "size-6",
} as const;

const textSizeClasses = {
  sm: "text-xs",
  default: "text-sm",
  lg: "text-base",
} as const;

const npsCategoryColors = {
  promoter: "text-emerald-600 dark:text-emerald-500",
  passive: "text-slate-600 dark:text-slate-400",
  detractor: "text-destructive",
} as const;

function parseStarRating(starRating?: number | string | null) {
  if (starRating == null || starRating === "") {
    return null;
  }

  const rating = Number(starRating);
  return Number.isFinite(rating) ? rating : null;
}

function formatStarRatingLabel(rating: number, maxStars: number) {
  const formattedRating = Number.isInteger(rating) ? rating.toString() : rating.toFixed(2).replace(/\.?0+$/, "");
  return `${formattedRating} out of ${maxStars} stars`;
}

function formatNpsCategoryLabel(npsCategory?: string | null) {
  if (!npsCategory) return null;
  if (npsCategory === "promoter") return "Promoter";
  if (npsCategory === "passive") return "Passive";
  if (npsCategory === "detractor") return "Detractor";
  return npsCategory.charAt(0).toUpperCase() + npsCategory.slice(1);
}

function formatNpsLabel(score: number, npsCategory?: string | null) {
  const categoryLabel = formatNpsCategoryLabel(npsCategory);
  return categoryLabel ? `NPS ${score} · ${categoryLabel}` : `NPS ${score}`;
}

function formatTenPointLabel(rating: number) {
  return `${rating} out of 10`;
}

function formatRecommendationLabel(recommended: boolean) {
  return recommended ? "Recommend" : "Do not recommend";
}

function MultiRatingTooltipContent({ labels }: { labels: string[] }) {
  if (labels.length === 1) {
    return <>{labels[0]}</>;
  }

  return (
    <div className="flex flex-col gap-0.5">
      {labels.map(label => (
        <div key={label}>{label}</div>
      ))}
    </div>
  );
}

function StarIcon({ fill, className }: { fill: number; className?: string }) {
  return (
    <span className={cn("relative inline-flex shrink-0", className)}>
      <Star className="size-full text-slate-300" strokeWidth={1.5} />
      {fill > 0 && (
        <span className="absolute inset-y-0 left-0 overflow-hidden" style={{ width: `${Math.min(fill, 1) * 100}%` }}>
          <Star className="size-full fill-amber-400 text-amber-400" strokeWidth={1.5} />
        </span>
      )}
    </span>
  );
}

function StarRatingVisual({ rating, maxStars = 5, size = "default", className, ...props }: StarRatingProps) {
  const clampedRating = Math.max(0, Math.min(maxStars, rating));

  return (
    <div className={cn("flex items-center", starGapClasses[size], className)} role="img" {...props}>
      {Array.from({ length: maxStars }, (_, index) => (
        <StarIcon
          key={index}
          fill={Math.min(1, Math.max(0, clampedRating - index))}
          className={starSizeClasses[size]}
        />
      ))}
    </div>
  );
}

function StarRating({ rating, maxStars = 5, size = "default", className, ...props }: StarRatingProps) {
  const clampedRating = Math.max(0, Math.min(maxStars, rating));
  const ratingLabel = formatStarRatingLabel(clampedRating, maxStars);

  return (
    <Tooltip>
      <Tooltip.Trigger
        render={
          <StarRatingVisual
            rating={rating}
            maxStars={maxStars}
            size={size}
            className={className}
            aria-label={ratingLabel}
            {...props}
          />
        }
      />
      <Tooltip.Content>{ratingLabel}</Tooltip.Content>
    </Tooltip>
  );
}

function TenPointRatingVisual({
  rating,
  size = "default",
  className,
  ...props
}: {
  rating: number;
  size?: StarRatingProps["size"];
  className?: string;
} & React.ComponentProps<"div">) {
  return (
    <div className={cn("flex items-center tabular-nums leading-none", textSizeClasses[size], className)} {...props}>
      <span className="font-bold text-indigo-600 dark:text-indigo-400">{rating}</span>
      <span className="font-semibold text-indigo-400 dark:text-indigo-500">/10</span>
    </div>
  );
}

function NpsRatingVisual({
  score,
  npsCategory,
  size = "default",
  className,
  ...props
}: {
  score: number;
  npsCategory?: string | null;
  size?: StarRatingProps["size"];
  className?: string;
} & React.ComponentProps<"span">) {
  const colorClass =
    npsCategory && npsCategory in npsCategoryColors
      ? npsCategoryColors[npsCategory as keyof typeof npsCategoryColors]
      : "text-slate-600";

  const iconSize = size === "sm" ? 14 : size === "lg" ? 20 : 16;

  return (
    <span className={cn("inline-flex items-center gap-1 font-bold tabular-nums", colorClass, className)} {...props}>
      <Gauge size={iconSize} strokeWidth={2.5} className="shrink-0" />
      <span className={textSizeClasses[size]}>{score}</span>
    </span>
  );
}

function RecommendationRatingVisual({
  recommended,
  size = "default",
  className,
  ...props
}: {
  recommended: boolean;
  size?: StarRatingProps["size"];
  className?: string;
} & React.ComponentProps<"span">) {
  const Icon = recommended ? ThumbsUp : ThumbsDown;

  return (
    <span className={cn("inline-flex", className)} role="img" {...props}>
      <Icon className={cn(sentimentIconSizeClasses[size], recommended ? "text-emerald-500" : "text-destructive")} />
    </span>
  );
}

function AdditionalRatingTypeIcon({
  type,
  recommended,
  npsCategory,
}: {
  type: ReviewType;
  recommended?: boolean;
  npsCategory?: string | null;
}) {
  const iconClassName = "size-3 shrink-0";

  if (type === "star") {
    return <Star className={cn(iconClassName, "fill-amber-400 text-amber-400")} strokeWidth={0} />;
  }
  if (type === "nps") {
    const npsColor =
      npsCategory && npsCategory in npsCategoryColors
        ? npsCategoryColors[npsCategory as keyof typeof npsCategoryColors]
        : "text-emerald-600";
    return <Gauge className={cn(iconClassName, npsColor)} strokeWidth={2.5} />;
  }
  if (type === "ten_point") {
    return <Hash className={cn(iconClassName, "text-indigo-500")} strokeWidth={2.5} />;
  }
  const ThumbIcon = recommended ? ThumbsUp : ThumbsDown;
  return (
    <ThumbIcon className={cn(iconClassName, recommended ? "text-emerald-500" : "text-destructive")} strokeWidth={2.5} />
  );
}

function RatingTypeVisual({
  type,
  size,
  parsedStarRating,
  npsScore,
  npsCategory,
  tenPointRating,
  hasRecommendation,
  recommended,
}: {
  type: ReviewType;
  size: StarRatingProps["size"];
  parsedStarRating: number | null;
  npsScore?: number | null;
  npsCategory?: string | null;
  tenPointRating?: number | null;
  hasRecommendation: boolean;
  recommended: boolean;
}) {
  if (type === "star" && parsedStarRating != null) {
    return <StarRatingVisual rating={parsedStarRating} size={size} />;
  }
  if (type === "nps" && npsScore != null) {
    return <NpsRatingVisual score={npsScore} npsCategory={npsCategory} size={size} />;
  }
  if (type === "ten_point" && tenPointRating != null) {
    return <TenPointRatingVisual rating={tenPointRating} size={size} />;
  }
  if (type === "text" && hasRecommendation) {
    return <RecommendationRatingVisual recommended={recommended} size={size} />;
  }
  return null;
}

function ReviewRating({
  review,
  size = "default",
  fallback = null,
  primaryRatingType = null,
  secondaryRatingLayout = "none",
  className,
  ...props
}: ReviewRatingProps) {
  const { starRating, sourceSentiment, tenPointRating, npsScore, npsCategory, ratingType } = review;
  const parsedStarRating = parseStarRating(starRating);
  const hasRecommendation = sourceSentiment === "positive" || sourceSentiment === "negative";
  const recommended = sourceSentiment === "positive";

  const availableTypes: ReviewType[] = [];
  if (parsedStarRating != null) availableTypes.push("star");
  if (npsScore != null) availableTypes.push("nps");
  if (tenPointRating != null) availableTypes.push("ten_point");
  if (hasRecommendation) availableTypes.push("text");

  const reviewPrimaryType =
    ratingType && availableTypes.includes(ratingType as ReviewType) ? (ratingType as ReviewType) : null;

  const primaryType: ReviewType | null =
    (primaryRatingType && availableTypes.includes(primaryRatingType) ? primaryRatingType : null) ??
    reviewPrimaryType ??
    availableTypes.find(type => type !== "text") ??
    (hasRecommendation ? "text" : null);

  const additionalTypes = availableTypes.filter(type => type !== primaryType);

  const labelForType = (type: ReviewType): string | null => {
    if (type === "star" && parsedStarRating != null) {
      return formatStarRatingLabel(Math.max(0, Math.min(5, parsedStarRating)), 5);
    }
    if (type === "nps" && npsScore != null) {
      return formatNpsLabel(npsScore, npsCategory);
    }
    if (type === "ten_point" && tenPointRating != null) {
      return formatTenPointLabel(tenPointRating);
    }
    if (type === "text" && hasRecommendation) {
      return formatRecommendationLabel(recommended);
    }
    return null;
  };

  const primaryVisual =
    primaryType != null ? (
      <RatingTypeVisual
        type={primaryType}
        size={size}
        parsedStarRating={parsedStarRating}
        npsScore={npsScore}
        npsCategory={npsCategory}
        tenPointRating={tenPointRating}
        hasRecommendation={hasRecommendation}
        recommended={recommended}
      />
    ) : null;

  if (!primaryVisual || !primaryType) {
    return fallback;
  }

  if (secondaryRatingLayout === "stacked") {
    const visibleTypes = [primaryType, ...additionalTypes];
    const labels = visibleTypes.map(labelForType).filter((label): label is string => label != null);

    const renderRating = (type: ReviewType) => {
      const label = labelForType(type);
      if (!label) return null;

      return (
        <Tooltip key={type}>
          <Tooltip.Trigger
            render={
              <div className="inline-flex items-center" aria-label={label}>
                <RatingTypeVisual
                  type={type}
                  size={size}
                  parsedStarRating={parsedStarRating}
                  npsScore={npsScore}
                  npsCategory={npsCategory}
                  tenPointRating={tenPointRating}
                  hasRecommendation={hasRecommendation}
                  recommended={recommended}
                />
              </div>
            }
          />
          <Tooltip.Content>{label}</Tooltip.Content>
        </Tooltip>
      );
    };

    const starType = visibleTypes.includes("star") ? ("star" as const) : null;
    const otherTypes = visibleTypes.filter(type => type !== "star");

    return (
      <div
        className={cn("inline-flex flex-col items-start gap-1.5", className)}
        aria-label={labels.join(", ")}
        {...props}
      >
        {starType && renderRating(starType)}
        {otherTypes.length > 0 && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {otherTypes.map(type => renderRating(type))}
          </div>
        )}
      </div>
    );
  }

  const showSecondary = secondaryRatingLayout === "compact";
  const labels = [labelForType(primaryType), ...additionalTypes.map(labelForType)].filter(
    (label): label is string => label != null
  );

  return (
    <Tooltip>
      <Tooltip.Trigger
        render={
          <div className={cn("inline-flex items-center gap-1.5", className)} aria-label={labels.join(", ")} {...props}>
            {primaryVisual}
            {showSecondary && additionalTypes.length > 0 && (
              <span className="flex flex-col items-center gap-px" aria-hidden>
                {additionalTypes.map(type => (
                  <AdditionalRatingTypeIcon
                    key={type}
                    type={type}
                    recommended={recommended}
                    npsCategory={npsCategory}
                  />
                ))}
              </span>
            )}
          </div>
        }
      />
      <Tooltip.Content>
        <MultiRatingTooltipContent labels={labels} />
      </Tooltip.Content>
    </Tooltip>
  );
}

StarRating.displayName = "StarRating";
ReviewRating.displayName = "ReviewRating";

export { StarRating, ReviewRating };
